import React, { useState, useEffect, useMemo } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeBankReportProperties,
  normalizeDate,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { PolicyReferenceProps } from "../../enum/policy-fetch-type.enum.ts";
import { useLocation } from "react-router-dom";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import { PolicyReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import BankReportDataGrid from "../../components/DataTables/BankReportDataGrid.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import moment from "moment";

const DATE_INDEX = 2;

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

const BankReport: React.FC<PolicyReferenceProps> = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
    selectedOffices: [],
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [officesOptions, setOfficesOptions] = useState<Option[]>([]);
  const [selectedOfficeValues, setSelectedOfficeValues] = useState<string[]>(
    []
  );
  const [selectedPolicies, setSelectedPolicies] = useState<
    [string, string, string][]
  >([]);
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      agent: "",
      selectedOffices: [],
    });
    setPolicies([]);
    setSelectedPolicies([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, officesRes, policyTypesRes] =
          await Promise.all([
            InsuranceService.getAllAgents(jwt),
            InsuranceService.getAllInsuranceCompanies(jwt),
            InsuranceService.getAllOffices(jwt),
            InsuranceService.getAllPolicyTypes(jwt),
          ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setOffices(officesRes as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    setOfficesOptions(
      Object.entries(offices).map(([k, v]) => ({
        text: v,
        value: k,
        id: k,
        selected: selectedOfficeValues.includes(k),
      }))
    );
  }, [offices, selectedOfficeValues]);

  const handleOfficeSelectionChange = (selectedValues: string[]) => {
    setSelectedOfficeValues(selectedValues);
    setFormState((prev) => ({ ...prev, selectedOffices: selectedValues }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { fromDate, toDate } = formState;
    if (!fromDate || !toDate) {
      setAlert("Моля, въведете стартова и крайна дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    setSelectedPolicies([]);
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchBankReport(
        {
          ...formState,
          fromDate: revertNormalizeDate(formState.fromDate),
          toDate: revertNormalizeDate(toDate),
        },
        jwt
      );
      const normalizedPolicies = Object.values(result).map((policy) => {
        const normalizedPolicy = normalizeBankReportProperties(policy);
        return {
          ...normalizedPolicy,
          installmentDate: normalizedPolicy.installmentDate
            ? normalizeDate(normalizedPolicy.installmentDate.trim())
            : "",
          paymentDate: "",
        };
      });
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  const totalCommissionAmount = useMemo(() => {
    return policies
      .filter((policy) =>
        selectedPolicies.some(
          ([polId, payNum]) =>
            polId === policy[PolicyReportMappingEnum.Pol_ID] &&
            payNum === policy[PolicyReportMappingEnum.Pay_Num]
        )
      )
      .reduce(
        (sum, policy) => sum + parseFloat(policy.installmentAmount || "0"),
        0
      )
      .toFixed(2);
  }, [policies, selectedPolicies]);

  const saveBankReportData = async () => {
    if (selectedPolicies.length === 0) {
      setAlert("Моля, изберете поне една полица за отчет.");
      setAlertType(FadeAlertType.error);
      return;
    }

    const hasMissingDates = selectedPolicies.some(
      (policy) => !policy[DATE_INDEX] || policy[DATE_INDEX].trim() === ""
    );

    if (hasMissingDates) {
      setAlert(
        "Не може да запишете отчет за полици без избрана дата на превод."
      );
      setAlertType(FadeAlertType.error);
      return;
    }

    try {
      const normalizedPolicies = selectedPolicies.map((policy) => {
        const policyCopy = [...policy] as [string, string, string];
        policyCopy[DATE_INDEX] = revertNormalizeDate(policyCopy[DATE_INDEX]);
        return policyCopy;
      });

      const result = await PolicyService.saveBankReport(
        normalizedPolicies,
        jwt
      );
      if (result) {
        setAlert("Успешен запис на отчет");
        setAlertType(FadeAlertType.success);
      }
    } catch (e) {
      setAlert("Грешка при запис на полица: " + e);
      setAlertType(FadeAlertType.error);
    }
  };

  const companyLabel = insuranceCompanies[formState.company] || "Всички";
  const agentLabel = agentOptions[formState.agent] || "Всички";
  const policyTypeLabel = policyTypes[formState.policyType] || "Всички";

  const selectedOfficeLabels =
    selectedOfficeValues.length > 0
      ? selectedOfficeValues
          .map((v) => offices[v])
          .filter(Boolean)
          .join(", ")
      : "Всички";

  const printReportMetadata = [
    { label: "Дата на печат", value: moment().format("DD/MM/YYYY HH:mm") },
    { label: "Период", value: `${formState.fromDate} – ${formState.toDate}` },
    { label: "ЗК", value: companyLabel },
    { label: "Вид полица", value: policyTypeLabel },
    { label: "Агент", value: agentLabel },
    { label: "Офис", value: selectedOfficeLabels },
  ];

  const printReportFooterMetadata = [
    {
      label: "Обща сума на премиите",
      value: `€${totalCommissionAmount} (${selectedPolicies.length} полици)`,
    },
  ];

  return (
    <>
      <Breadcrumb pageName="Отчет по банка" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Отчет по банка" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <MultiSelect
                options={officesOptions}
                setOptions={setOfficesOptions}
                placeholder="Изберете офис"
                onSelectionChange={handleOfficeSelectionChange}
                id="multi-select-office"
              />
            </div>
          </div>

          <div className="mt-2 flex justify-center">
            <button
              type="submit"
              className="bg-primary w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>

        <BankReportDataGrid
          policies={policies}
          isLoading={isLoading}
          selectedPolicies={selectedPolicies}
          setPolicies={setPolicies}
          setSelectedPolicies={setSelectedPolicies}
          tableName={"Отчетени полици по банка"}
          fileName={`Отчетени полици по банка - ${moment().format(
            "DD/MM/YYYY HH:mm"
          )}`}
          printReportMetadata={printReportMetadata}
          printReportFooterMetadata={printReportFooterMetadata}
        />

        <div className="p-3">
          <div className="grid grid-cols-3 items-center gap-2">
            <div>
              <label className={labelClass}>Обща сума на премиите</label>
              <span className="block px-2 py-1 text-sm font-bold text-black dark:text-white">
                €{totalCommissionAmount}
              </span>
            </div>
            <div className="col-start-2 flex justify-center">
              <button
                type="button"
                onClick={saveBankReportData}
                className="bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Запис
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BankReport;
